'use strict';

import _ from 'underscore';
import {Collection} from 'backbone';
import * as utilities from '../js/utilities';
import * as featureFlags from '../utilities/featureFlags';
import config from '../js/config';
import {SBO_ALLOCATION_MODE, SBO_ERRORS, SBO_PACKAGING_MODE} from '../components/SingleBuyingOptions/SingleBuyingOptionConstants';
import SingleBuyingOptionsRegionCollection from '../components/SingleBuyingOptions/SingleBuyingOptionsRegionCollection';


/**
 * Get the last buying options minimum quantity (smallest orderMinimumQuantity) if it was not valid
 *
 * @param {SingleBuyingOptionsModel} model
 * @param {number} quantity
 * @returns {number}
 * @private
 */
const _getQuantityOrDefault = function(model, quantity) {
  if (quantity) {
    return quantity;
  }

  let lowestBuyingOption = _.min(model.getAddToCartBuyingOptionsForAllRegion().models, buyingOption => buyingOption.get('orderMinimumQuantity'));
  return Math.max(lowestBuyingOption.get('orderMinimumQuantity'), lowestBuyingOption.get('orderIncrement'));
};


/**
 * Validate an individual buying option.
 *
 * @param {number} quantity
 * @param {number} quantityLeft
 * @param {BuyingOptionModel} buyingOption
 * @param {string} [mode=SBO_ALLOCATION_MODE.DEFAULT] - The allocation mode.
 * @returns {{errors: Array, metMinimum: boolean}}
 * @private
 */
const _validateBuyingOption = function(quantity, quantityLeft, buyingOption, mode = SBO_ALLOCATION_MODE.DEFAULT) {
  const orderMinimumQuantity = buyingOption.get('orderMinimumQuantity');
  const orderIncrement = buyingOption.get('orderIncrement');
  const orderStock = buyingOption.get('quantity');
  const sourcePartId = buyingOption.get('sourcePartId');
  const orderMaximumQuantity = buyingOption.get('orderMaximumQuantity');

  let result = {
    errors: [],
    metMinimum: true
  };

  // Validate minimum
  if (quantity < orderMinimumQuantity) {
    result.errors.push(SBO_ERRORS.MINIMUM_QUANTITY);
  }

  // Validate maximum
  if (mode === SBO_ALLOCATION_MODE.DEFAULT && quantity > orderStock) {
    result.errors.push(SBO_ERRORS.MAXIMUM_QUANTITY);
  }

  // Validate increment
  if (!result.errors.length && orderIncrement !== 1 && quantityLeft % orderIncrement) {
    result.errors.push(SBO_ERRORS.INVALID_INCREMENT);
  }

  // Not enough ordered
  if (quantityLeft < orderMinimumQuantity || quantityLeft < orderIncrement) {
    result.metMinimum = false;
  }

  if (sourcePartId.indexOf('-AR') > 0 && quantityLeft > orderMaximumQuantity && result.errors.indexOf(SBO_ERRORS.MAXIMUM_QUANTITY) === -1) {
    result.errors.push(SBO_ERRORS.MAXIMUM_QUANTITY);
  }

  return result;
};


/**
 * Reduce buying options to quantities.
 *
 * @param {{quantity: number, quantityLeft: number, quantities: object, errors: string[], mode: string}} memo
 * @param {BuyingOptionModel} buyingOption
 * @returns {{quantity: number, quantityLeft: number, quantities: object, errors: string[], mode: string}}
 * @private
 */
const _reduceBuyingOptionsToQuantities = function(memo, buyingOption) {
  const mode = memo.mode;
  const sourcePartId = buyingOption.get('sourcePartId');
  const orderIncrement = buyingOption.get('orderIncrement');
  const orderStock = buyingOption.get('quantity');
  const orderMaximumQuantity = buyingOption.get('orderMaximumQuantity');

  memo.quantities[sourcePartId] = 0;

  // No more stock to allocate
  if (memo.quantityLeft === 0) {
    return memo;
  }

  let valid = _validateBuyingOption(memo.quantity, memo.quantityLeft, buyingOption, mode);

  // Only check for errors when not backordering
  // Add error messages if there were any

  _.forEach(valid.errors, type => memo.errors.push({ sourcePartId, type }));

  // Not enough ordered
  if (!valid.metMinimum) {
    return memo;
  }


  let max = Math.floor(memo.quantityLeft / orderIncrement) * orderIncrement;

  // There isn't enough stock - so take what we can
  if (max > orderStock && mode !== SBO_ALLOCATION_MODE.BACKORDER_INFINITE) {
    max = orderStock;
  }

  if (featureFlags.EnableArrowReel.isEnabled() && buyingOption.get('isArrowReel')) {
    if (orderMaximumQuantity >= memo.quantityLeft && buyingOption.get('isChecked') && memo.quantityLeft <= orderStock) {
      memo.quantities[sourcePartId] = max;
      memo.quantityLeft = memo.quantityLeft - max;
    }

  } else {
    if (featureFlags.EnableArrowReel.isEnabled() && buyingOption.get('isChecked') && !buyingOption.get('isReelPackagingType')) {
      memo.quantities[sourcePartId] = max;
      memo.quantityLeft = memo.quantityLeft - max;
    }
    else {
      memo.quantities[sourcePartId] = max;
      memo.quantityLeft = memo.quantityLeft - max;
    }
  }

  return memo;
};


/**
 * Attempt to recursively allocate the stock for buying options.
 *
 * @param {number} quantity - The quantity desired.
 * @param {BuyingOptionModel[]} buyingOptions - The buying options we are checking.
 * @param {string} [mode=SBO_ALLOCATION_MODE.DEFAULT] - The allocation mode.
 * @param {object} [firstStockAllocation] - The first attempt if made.
 * @returns {{quantity: number, quantityLeft: number, quantities: object, errors: string[], mode: string}}
 * @private
 */
const _recursivelyAttemptToAllocateStock = function(quantity, buyingOptions, mode = SBO_ALLOCATION_MODE.DEFAULT, firstStockAllocation = null) {
  let attemptedStockAllocation = _.reduce(buyingOptions, _reduceBuyingOptionsToQuantities, {
    quantity,
    quantityLeft: quantity,
    quantities: {},
    errors: [],
    mode
  });

  // Successful stock allocation
  if (!attemptedStockAllocation.errors.length || !attemptedStockAllocation.quantityLeft) {
    // We used all the stock, so remove the error messages
    attemptedStockAllocation.errors = [];
    _.each(buyingOptions, (option) => {
      _.each(Object.keys(attemptedStockAllocation.quantities), (quantity) => {
        if (option.id === quantity) {
          if (attemptedStockAllocation.quantities[quantity] % option.get('orderIncrement')) {
            attemptedStockAllocation.errors.push({
              sourcePartId: quantity,
              type: SBO_ERRORS.INVALID_INCREMENT
            });
          }
        }
      });
    });
    return attemptedStockAllocation;
  }

  if (buyingOptions.length === 1) {
    // The firstStockAllocation is used to display the correct original errors
    return firstStockAllocation || attemptedStockAllocation;
  }

  // Remove the first one so we can try and fit the stock again
  buyingOptions.shift();

  return _recursivelyAttemptToAllocateStock(quantity, buyingOptions, mode, firstStockAllocation === null ? attemptedStockAllocation : firstStockAllocation);
};

/**
 * Sort Buying Options By Region
 *
 * @param {BuyingOptionModel[]} buyingOptions
 * @returns {{sortedBuyingOptions: Array}}
 * @private
 */
const _sortBuyingOptionsByRegions = function(buyingOptions) {

  var sortedBuyingOptions = [];

  const groupedByRegion = buyingOptions.reduce((acc, option) => {
    let inventoryRegion = option.get('inventoryRegion');
    if (!acc[inventoryRegion]) {
      acc[inventoryRegion] = [];
    }
    acc[inventoryRegion].push(option);
    return acc;
  }, {});

  //For Each Region it will sort by orderMinimumQuantity from High to Low
  Object.keys(groupedByRegion).map(region => {
    groupedByRegion[region].sort((a, b) => b.get('orderMinimumQuantity') - a.get('orderMinimumQuantity'));
  });

  if (groupedByRegion.NAC) {
    //By Default it will pick First NAC Region
    for (let nacBuyingOption of groupedByRegion.NAC) {
      sortedBuyingOptions.push(nacBuyingOption);
    }

    delete groupedByRegion.NAC;
  }

  //Calculating total Quantity of each Region
  const regionsTotalQuantity = Object.keys(groupedByRegion).map(region => {
    let totalQuantity = groupedByRegion[region].reduce((acc, option) => acc + option.get('quantity'), 0);
    return { region, totalQuantity };
  });

  //Sort Regions based on Total Quantity from low to High
  regionsTotalQuantity.sort((a, b) => a.totalQuantity - b.totalQuantity);

  for (let item of regionsTotalQuantity) {
    let region = item.region;
    sortedBuyingOptions = [...sortedBuyingOptions, ...groupedByRegion[region]];
  }

  return sortedBuyingOptions;
}

/**
 * Attempt to allocate the stock for all of the buying options.
 *
 * @param {SingleBuyingOptionsModel} model
 * @param {number} quantity - The quantity desired.
 * @param {string[]} [sourcePartIds] - Optional sourcePartIds to filter buying options by.
 * @param {string} [mode=SBO_ALLOCATION_MODE.DEFAULT] - The allocation mode.
 * @returns {{quantity: number, quantityLeft: number, quantities: object, errors: string[], mode: string}}
 * @private
 */
const _attemptToAllocateStock = function(model, quantity, sourcePartIds, mode = SBO_ALLOCATION_MODE.DEFAULT) {
  let addToCartOptions = model.getAddToCartBuyingOptionsForAllRegion();
  let buyingOptions = addToCartOptions.models;

  // Only use the given sourcePartIds if supplied, otherwise all buying options
  if (sourcePartIds) {
    buyingOptions = _.map(sourcePartIds, sourcePartId => addToCartOptions.get(sourcePartId));
  }

  // Sort the buying options by orderMinimumQuantity high to low
  if (featureFlags.EnableArrowReel.isEnabled()) {
    buyingOptions = _.sortBy(buyingOptions, buyingOption => -buyingOption.get('isSelected'));
  }

  buyingOptions = _sortBuyingOptionsByRegions(buyingOptions);

  let stockAllocation = _recursivelyAttemptToAllocateStock(quantity, buyingOptions, mode);

  // If we missing some buying options - ensure the result contains 0 counts for the missing sourcePartIds
  if (_.size(stockAllocation.quantities) < addToCartOptions.length) {
    addToCartOptions.forEach(buyingOptionModel => {
      let sourcePartId = buyingOptionModel.get('sourcePartId');
      if (!stockAllocation.quantities[sourcePartId]) {
        stockAllocation.quantities[sourcePartId] = 0
      }
    });
  }

  return stockAllocation;
};

/**
 * Attempt to allocate the stock for all of the buying options - the remaining quantity as a backorder
 *
 * @param {SingleBuyingOptionsModel} model
 * @param {number} quantity - The quantity desired.
 * @param {string[]} [sourcePartIds] - Optional sourcePartIds to filter buying options by.
 * @returns {{quantity: number, quantityLeft: number, quantities: object, errors: string[], backorder: number}}
 * @private
 */
const _attemptToAllocateStockWithBackorder = function(model, quantity, sourcePartIds) {
  let stockAllocation = _attemptToAllocateStock(model, quantity, sourcePartIds);
  stockAllocation.errors = [];

  // If we can't allocate the required quantity
  if (stockAllocation.quantityLeft) {

    let addToCartOptions = model.getAddToCartBuyingOptionsForCurrentRegion();

    addToCartOptions.forEach(buyingOptionModel => {
      let valid = _validateBuyingOption(quantity, stockAllocation.quantityLeft, buyingOptionModel, SBO_ALLOCATION_MODE.BACKORDER);
      let sourcePartId = buyingOptionModel.get('sourcePartId');
      let total = buyingOptionModel.get('quantity');
      let parsedQuantity = model.get('parsedQuantity');
      _.forEach(valid.errors, type => {
        if (parsedQuantity < total) {
          stockAllocation.errors = [{ sourcePartId, type }];
        }
      });
    });

    stockAllocation.backorder = stockAllocation.quantityLeft;
    stockAllocation.quantityLeft = 0;
  }

  return stockAllocation;
};


/**
 * @param {object} results
 * @param {BuyingOptionModel[]} individualBuyingOptionModels
 * @param {BuyingOptionModel[]} selectedBuyingOptionModels
 * @private
 */
const _getSelectableRadioBuyingOptions = function(results, individualBuyingOptionModels, selectedBuyingOptionModels) {
  if (individualBuyingOptionModels.length > 1) {
    results.mode = SBO_PACKAGING_MODE.RADIO;

    // What is missing from selected vs valid
    _.difference(individualBuyingOptionModels, selectedBuyingOptionModels).forEach(buyingOption => {
      results.selectable[buyingOption.get('sourcePartId')] = true;
    });

    // What is in both valid and selected
    _.intersection(individualBuyingOptionModels, selectedBuyingOptionModels).forEach(buyingOption => {
      results.deSelectable[buyingOption.get('sourcePartId')] = true;
    });
  }
};


/**
 * @param {SingleBuyingOptionsModel} model
 * @param {object} results
 * @param {BuyingOptionModel[]} individualBuyingOptionModels
 * @param {BuyingOptionModel[]} combinedBuyingOptionModels
 * @param {BuyingOptionModel[]} selectedBuyingOptionModels
 * @private
 */
const _getSelectableCheckboxBuyingOptions = function(model, results, individualBuyingOptionModels, combinedBuyingOptionModels, selectedBuyingOptionModels) {
  if (individualBuyingOptionModels.length === 1 && combinedBuyingOptionModels.length > 1) {
    results.mode = SBO_PACKAGING_MODE.CHECKBOX;

    // What is missing from selectedBuyingOptionModels
    _.difference(model.getAddToCartBuyingOptionsForCurrentRegion().models, selectedBuyingOptionModels)
      .forEach(buyingOption => {
        results.selectable[buyingOption.get('sourcePartId')] = true;
      });

    // What is extra in selected from valid
    _.difference(selectedBuyingOptionModels, individualBuyingOptionModels).forEach(buyingOption => {
      results.deSelectable[buyingOption.get('sourcePartId')] = true;
    });
  }
};


/**
 * Calculates the price and part allocation for each buying option.
 *
 * @param {SingleBuyingOptionsModel} model
 * @param {string} [inputQuantity = '']
 * @param {boolean} [allowBackorder = false]
 * @param {string[]=} sourcePartIds
 * @returns {{isValid: boolean, isPurchasable: boolean, errors: string[], quantities: object, backorder: number}}
 */
export const getQuantitiesForBuyingOptions = function(model, inputQuantity = '', allowBackorder = false, sourcePartIds) {
  let parsedQuantity = utilities.parseQuantity(inputQuantity);

  // Invalid quantity
  if (inputQuantity !== '' && (parsedQuantity === 0 || !utilities.isInteger(parsedQuantity))) {
    return {
      inputQuantity,
      parsedQuantity,
      isValid: false,
      isPurchasable: false,
      errors: [{ type: SBO_ERRORS.INVALID_QUANTITY }],
      quantities: {},
      backorder: 0
    };
  }

  // Get the last buying options minimum quantity (smallest orderMinimumQuantity) if it was not valid
  let quantity = _getQuantityOrDefault(model, parsedQuantity);

  let stockAllocation;
  if (allowBackorder) {
    stockAllocation = _attemptToAllocateStockWithBackorder(model, quantity, sourcePartIds);
  } else {
    stockAllocation = _attemptToAllocateStock(model, quantity, sourcePartIds);
  }

  let isValid = !stockAllocation.errors.length;
  let isPurchasable = (isValid && parsedQuantity) > 0;
  let priceForQuantity = quantity;

  // Adjust the price for display to not include the backorder quantity
  if (stockAllocation.backorder) {
    priceForQuantity = quantity - stockAllocation.backorder;
  }

  return {
    inputQuantity,
    parsedQuantity,
    priceForQuantity: priceForQuantity,
    isValid,
    isPurchasable,
    errors: stockAllocation.errors.reverse(),
    quantities: stockAllocation.quantities,
    backorder: stockAllocation.backorder || 0
  };
};

/**
 * Set the custom reel checkbox selection based on the Cut Strip and stock allocation
 * @param {object} results
 * @param {BuyingOptionModel[]} model
 * @param {object[]} quantities
 * @private
 */
const _updateCustomReelBuyingOption = function(model, results, quantities) {
  if (featureFlags.EnableArrowReel.isEnabled()) {
    //get custom reel buying option
    let arrowReelBuyingOption = model.getArrowReelBuyingOptionForCurrentRegion();
    if (arrowReelBuyingOption) {
      let arrowReelSourcePartId = arrowReelBuyingOption.get('sourcePartId');
      let cutStripSourcePartId = arrowReelSourcePartId.replace(config.customReel.sourcePartIdSuffix, '');
      if (arrowReelBuyingOption.get('isChecked')) {
        if (quantities[arrowReelSourcePartId] > 0 && quantities[arrowReelSourcePartId] <= arrowReelBuyingOption.get('orderMaximumQuantity')) {
          results.selectable[cutStripSourcePartId] = true;
        }
      }
      else {
        if (quantities[cutStripSourcePartId] > 0 && quantities[cutStripSourcePartId] <= arrowReelBuyingOption.get('orderMaximumQuantity')) {
          results.selectable[arrowReelSourcePartId] = true;
        }
      }
      //Arrow reel selectable by checkbox mode but quantities of arrow reel max
      if (quantities[arrowReelSourcePartId] === 0 && quantities[cutStripSourcePartId] > 0 && quantities[cutStripSourcePartId] > arrowReelBuyingOption.get('orderMaximumQuantity')) {
        results.selectable[arrowReelSourcePartId] = false;
      }
    }
  }
};

/**
 * Get the selectable information to allow choosing specific buying options.
 *
 * @param {SingleBuyingOptionsModel} model
 * @param {number} quantity
 * @param {object} selectedQuantities
 * @param {boolean} [allowBackorder = false]
 * @returns {{mode: string, selectable: object, deSelectable: object}}
 */
export const getSelectableBuyingOptions = function(model, quantity, selectedQuantities, allowBackorder = false) {

  let results = { mode: SBO_PACKAGING_MODE.NONE };
  let mode = SBO_ALLOCATION_MODE.DEFAULT;

  if (allowBackorder) {
    mode = SBO_ALLOCATION_MODE.BACKORDER_INFINITE;
  }

  let addToCartOptions = model.getAddToCartBuyingOptionsForAllRegion();

  // Check if each individual buying option is valid
  let individualBuyingOptionModels = addToCartOptions.chain()
    .filter(buyingOption => {
      let valid = _validateBuyingOption(quantity, quantity, buyingOption, mode);
      return valid.errors.length === 0 && valid.metMinimum;
    })
    .value();

  // Use existing logic to see if we can combine buying options
  let quantities = getQuantitiesForBuyingOptions(model, quantity, allowBackorder).quantities;

  let combinedBuyingOptionModels = _.chain(quantities)
    .map((qty, sourcePartId) => [sourcePartId, !!qty])
    .filter(arr => arr[1])
    .map(arr => model.getBuyingOption(arr[0]))
    .value();

  // Get an array of the selected Buying options
  let selectedBuyingOptionModels = _.reduce(selectedQuantities, (q, val, sourcePartId) => {
    if (val) {
      q.push(model.getBuyingOption(sourcePartId));
    }
    return q;
  }, []);

  // Default all buying options to false > {1234: false, 5456: false}
  results.selectable = addToCartOptions.chain().map(p => [p.get('sourcePartId'), false]).object().value();
  results.deSelectable = _.clone(results.selectable);

  if (allowBackorder) {
    // Check the option is at least meeting the minimum order quantity
    _.forEach(results.selectable, (v, k) => {
      let buyingOption = model.getBuyingOption(k);
      if (quantity >= buyingOption.get('orderMinimumQuantity')) {
        results.selectable[k] = true;
      }
    });

    // Can't un-select the last option
    if (selectedBuyingOptionModels.length === 1) {
      let selectedBuyingOption = selectedBuyingOptionModels[0];
      results.selectable[selectedBuyingOption.id] = false;
    }

    results.mode = (individualBuyingOptionModels.length > combinedBuyingOptionModels.length) ? SBO_PACKAGING_MODE.RADIO : SBO_PACKAGING_MODE.CHECKBOX;

    return results;
  }

  // Radio mode
  _getSelectableRadioBuyingOptions(results, individualBuyingOptionModels, selectedBuyingOptionModels);

  // Checkbox mode
  _getSelectableCheckboxBuyingOptions(model, results, individualBuyingOptionModels, combinedBuyingOptionModels, selectedBuyingOptionModels);

  //Custom Reel Radio Mode
  _updateCustomReelBuyingOption(model, results, quantities);

  return results;
};



// WEBPACK FOOTER //
// ./assets/services/SingleBuyingOptionsService.js